<template>
  <v-main>
    <v-container class="privacy-policy-container">
      <!-- Header -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="policy-header">
            <h1 class="policy-title">Privacy Policy</h1>
            <div class="policy-meta">
              <v-chip small color="primary" class="mr-2">
                <v-icon small left>mdi-calendar</v-icon>
                Effective Date: June 1, 2026
              </v-chip>
              <v-chip small color="secondary">
                <v-icon small left>mdi-update</v-icon>
                Last Updated: June 1, 2026
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Table of Contents -->
      <v-row class="mb-4">
        <v-col cols="12" md="3">
          <v-card class="toc-card sticky-toc" elevation="2">
            <v-card-title class="subtitle-1">
              <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
              Quick Navigation
            </v-card-title>
            <v-card-text class="pa-0">
              <v-list dense>
                <v-list-item
                  v-for="section in sections"
                  :key="section.id"
                  @click="scrollToSection(section.id)"
                  class="toc-item"
                >
                  <v-list-item-content>
                    <v-list-item-title class="toc-link">
                      {{ section.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card class="policy-content" elevation="2">
            <v-card-text class="pa-6">
              <!-- Introduction -->
              <div id="introduction" class="section">
                <h2 class="section-title">Introduction</h2>
                <p class="policy-text">
                  Welcome to Healthy Tantra ("we," "our," "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how information associated with your use of Healthy Tantra's mobile applications, website, web portals, and related services (collectively, the "Platform") is handled.
                </p>
                <p class="policy-text">
                  Healthy Tantra is a comprehensive healthcare and wellness platform that connects users with verified healthcare professionals, enables medicine and wellness product ordering, and provides access to yoga, wellness, and related services. We understand the sensitive nature of health information and take our responsibility to protect your data seriously.
                </p>
                <p class="policy-text">
                  This Privacy Policy applies to all users of the Healthy Tantra platform, including patients, customers, wellness participants, and visitors using our Platform and related services.
                </p>
                <p class="policy-text">
                  Use of the Platform is also subject to Healthy Tantra's Terms of Service.
                </p>
                <p class="policy-text">
                  Information handling practices may vary depending on applicable laws and regulations in the regions where Healthy Tantra operates.
                </p>
                <v-alert type="warning" prominent border="left" class="my-4">
                  <strong>By using the Healthy Tantra Platform, you agree to the practices described in this Privacy Policy. If you do not agree with this Privacy Policy, please do not use the Platform.</strong>
                </v-alert>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 1. Information You May Provide -->
              <div id="information-collect" class="section">
                <h2 class="section-title">1. Information You May Provide</h2>

                <h3 class="subsection-title">1.1 Personal Information</h3>
                <p class="policy-text">When creating an account or using our services, you may voluntarily provide information including:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in personalInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="primary" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <h3 class="subsection-title">1.2 Health and Medical Information</h3>
                <p class="policy-text">To receive healthcare and wellness services through the Platform, you may choose to provide, upload, share, or maintain health-related information including:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in healthInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="success" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-alert type="info" dense border="left" class="my-3">
                  Certain health-related information may constitute sensitive personal data under applicable laws and may be processed only for healthcare delivery, wellness services, operational purposes, safety, and legal compliance.
                </v-alert>

                <h3 class="subsection-title">1.3 Financial Information</h3>
                <p class="policy-text">For payments, billing, and transaction management, information associated with purchases and payments may include:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in financialInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="warning" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-alert type="success" prominent border="left" class="my-4">
                  <strong>Important:</strong> Healthy Tantra does NOT directly store or process your credit card numbers, debit card numbers, CVV codes, UPI PINs, or banking credentials. Payment-related processing is handled securely by third-party payment providers such as Razorpay, which is PCI-DSS compliant.
                </v-alert>

                <h3 class="subsection-title">1.4 Location Information</h3>
                <p class="policy-text">To enable deliveries and related services, you may provide:</p>
                <v-list dense class="info-list">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="info" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title><strong>Delivery Addresses</strong> (street address, locality, city, state, pincode)</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="info" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title><strong>Receiver Name and Phone Number</strong> (for delivery purposes)</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <p class="policy-text">
                  Healthy Tantra does not continuously track or monitor your real-time GPS location unless explicitly permitted by you for a specific feature or service.
                </p>
                <p class="policy-text">
                  Approximate location information may sometimes be inferred from delivery addresses, IP addresses, or device settings where applicable.
                </p>

                <h3 class="subsection-title">1.5 Communications</h3>
                <p class="policy-text">Communications and interactions through the Platform may include:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in communicationInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="purple" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-alert type="info" dense border="left" class="my-3">
                  Consultation communications may be retained for medical record continuity, quality assurance, dispute resolution, safety, and legal compliance purposes.
                </v-alert>

                <h3 class="subsection-title">1.6 Device and Technical Information</h3>
                <p class="policy-text">Certain technical and device-related information may automatically become available while using the Platform, including:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in deviceInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="teal" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <h3 class="subsection-title">1.7 Usage Information</h3>
                <p class="policy-text">Information related to Platform usage and interactions may include:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in usageInfo" :key="item">
                    <v-list-item-icon>
                      <v-icon color="orange" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="item"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 2. How Your Information Is Used -->
              <div id="how-we-use" class="section">
                <h2 class="section-title">2. How Your Information Is Used</h2>
                <p class="policy-text">Information associated with your use of the Platform may be used for the following purposes:</p>

                <div v-for="use in informationUses" :key="use.title" class="use-case mb-4">
                  <h3 class="subsection-title">{{ use.title }}</h3>
                  <v-list dense class="info-list">
                    <v-list-item v-for="item in use.items" :key="item">
                      <v-list-item-icon>
                        <v-icon :color="use.color" small>mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ item }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 3. How Information May Be Shared -->
              <div id="how-we-share" class="section">
                <h2 class="section-title">3. How Information May Be Shared</h2>
                <p class="policy-text">Healthy Tantra respects user privacy and limits information sharing to the circumstances described below.</p>

                <h3 class="subsection-title">3.1 With Healthcare Professionals</h3>
                <p class="policy-text">When healthcare services are requested through the Platform, relevant information may be made available to the selected healthcare professional to support consultation, diagnosis, treatment, follow-up care, and continuity of healthcare services, including:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in sharedWithDoctors" :key="item">
                    <v-list-item-icon>
                      <v-icon color="blue" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-alert type="info" dense border="left" class="my-3">
                  Healthcare professionals available through the Platform are independent practitioners responsible for their own professional services, medical advice, diagnoses, and treatment decisions. Healthcare professionals are expected to maintain confidentiality and handle information in accordance with applicable professional, ethical, and legal standards.
                </v-alert>

                <h3 class="subsection-title">3.2 With Service Providers</h3>
                <p class="policy-text">Healthy Tantra works with trusted third-party service providers that support Platform functionality and service delivery, including:</p>

                <v-expansion-panels class="my-4">
                  <v-expansion-panel v-for="provider in serviceProviders" :key="provider.name">
                    <v-expansion-panel-header>
                      <div class="d-flex align-center">
                        <v-icon :color="provider.color" class="mr-3">{{ provider.icon }}</v-icon>
                        <strong>{{ provider.name }}</strong>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <p><strong>Purpose:</strong> {{ provider.purpose }}</p>
                      <p>
                        <strong>Privacy Policy:</strong>
                        <a :href="provider.privacyUrl" target="_blank" class="policy-link">
                          {{ provider.privacyUrl }}
                          <v-icon small>mdi-open-in-new</v-icon>
                        </a>
                      </p>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <p class="policy-text">
                  These service providers may access limited information only as necessary to perform services on behalf of Healthy Tantra and are expected to maintain appropriate security and confidentiality measures.
                </p>

                <h3 class="subsection-title">3.3 With User Authorization or Request</h3>
                <p class="policy-text">Information may be shared when explicitly requested or authorized by the user, including:</p>
                <v-list dense class="info-list">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="green" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Sharing prescriptions, medical documents, or consultation details with healthcare professionals or caregivers specified by the user</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon color="green" small>mdi-circle-small</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Providing information necessary to fulfill user-requested services through the Platform</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <p class="policy-text">
                  Where required under applicable laws, additional consent may be requested before sharing sensitive personal or health-related information beyond healthcare delivery, operational support, legal compliance, or safety purposes.
                </p>
                <p class="policy-text">
                  Consent for certain information-sharing activities may be withdrawn at any time, subject to applicable legal, operational, healthcare record retention, or contractual obligations.
                </p>

                <v-alert type="error" prominent border="left" class="my-4">
                  <strong>Healthy Tantra does NOT sell personal information or medical information to third parties for advertising or marketing purposes.</strong>
                </v-alert>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 4. Data Security -->
              <div id="data-security" class="section">
                <h2 class="section-title">4. Data Security</h2>
                <p class="policy-text">Reasonable and industry-standard security measures are implemented to help protect information associated with the Platform.</p>

                <h3 class="subsection-title">4.1 Technical Security Measures</h3>
                <v-list dense class="info-list">
                  <v-list-item v-for="measure in technicalSecurity" :key="measure">
                    <v-list-item-icon>
                      <v-icon color="green" small>mdi-shield-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title v-html="measure"></v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <h3 class="subsection-title">4.2 Organizational Security Measures</h3>
                <v-list dense class="info-list">
                  <v-list-item v-for="measure in orgSecurity" :key="measure">
                    <v-list-item-icon>
                      <v-icon color="blue" small>mdi-shield-account</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ measure }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <h3 class="subsection-title">4.3 User Security Best Practices</h3>
                <p class="policy-text">Users are encouraged to:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="practice in userSecurity" :key="practice">
                    <v-list-item-icon>
                      <v-icon color="orange" small>mdi-account-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ practice }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-alert type="warning" border="left" class="my-4">
                  <strong>While reasonable efforts are made to protect information, no electronic transmission or storage system can be guaranteed to be completely secure.</strong>
                </v-alert>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 5. Your Rights and Choices -->
              <div id="your-rights" class="section">
                <h2 class="section-title">5. Your Rights and Choices</h2>
                <p class="policy-text">Subject to applicable laws, users may have the following rights regarding their information.</p>

                <v-row>
                  <v-col cols="12" md="6" v-for="right in userRights" :key="right.title">
                    <v-card outlined class="right-card">
                      <v-card-title class="subtitle-1">
                        <v-icon :color="right.color" left>{{ right.icon }}</v-icon>
                        {{ right.title }}
                      </v-card-title>
                      <v-card-text>
                        <p class="policy-text" v-for="desc in right.description" :key="desc">{{ desc }}</p>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 6. Data Retention -->
              <div id="data-retention" class="section">
                <h2 class="section-title">6. Data Retention</h2>
                <p class="policy-text">Information may be retained for as long as reasonably necessary to:</p>
                <v-list dense class="info-list">
                  <v-list-item v-for="item in retentionReasons" :key="item">
                    <v-list-item-icon>
                      <v-icon color="indigo" small>mdi-database-clock</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ item }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <p class="policy-text">
                  Retention periods may vary depending on the nature of the information and applicable legal requirements.
                </p>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 7. Children's Privacy -->
              <div id="childrens-privacy" class="section">
                <h2 class="section-title">7. Children's Privacy</h2>
                <p class="policy-text">
                  Parents or legal guardians may create dependent profiles for minors under their supervision.
                </p>
                <p class="policy-text">
                  If information relating to minors is found to have been provided unlawfully or without appropriate authorization, reasonable steps may be taken to restrict or remove such information in accordance with applicable laws.
                </p>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 8. Changes to This Privacy Policy -->
              <div id="changes" class="section">
                <h2 class="section-title">8. Changes to This Privacy Policy</h2>
                <p class="policy-text">
                  This Privacy Policy may be updated periodically.
                </p>
                <p class="policy-text">
                  Updated versions will be made available through the Platform along with a revised effective date. Continued use of the Platform after updates become effective constitutes acceptance of the revised Privacy Policy.
                </p>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- 9. Contact Us -->
              <div id="contact" class="section">
                <h2 class="section-title">9. Contact Us</h2>
                <v-card outlined class="contact-card">
                  <v-card-text>
                    <p class="policy-text">
                      If you have any questions, concerns, or requests regarding this Privacy Policy or information handling practices, please contact:
                    </p>
                    <div class="contact-details mt-4">
                      <div class="contact-item">
                        <v-icon color="primary" left>mdi-domain</v-icon>
                        <strong>Healthy Tantra</strong>
                      </div>
                      <div class="contact-item">
                        <v-icon color="primary" left>mdi-email</v-icon>
                        <strong>Email:</strong>
                        <a href="mailto:Care@healthytantra.com" class="policy-link">Care@healthytantra.com</a>
                      </div>
                      <div class="contact-item">
                        <v-icon color="primary" left>mdi-message-text</v-icon>
                        <strong>Support:</strong> In-Platform support ticket system
                      </div>
                      <div class="contact-item">
                        <v-icon color="primary" left>mdi-clock</v-icon>
                        <strong>Response Time:</strong> Within 30 days of receipt
                      </div>
                    </div>
                    <v-alert type="info" dense class="mt-4">
                      For urgent matters, please include "URGENT" in your subject line.
                    </v-alert>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Footer Note -->
              <div class="policy-footer mt-8">
                <v-divider class="mb-4"></v-divider>
                <p class="text-center font-weight-bold">
                  Thank you for trusting Healthy Tantra with your health and wellness journey.
                </p>
                <p class="text-center text-caption grey--text">
                  Your privacy and security are our top priorities.
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Back to Top Button -->
      <v-fab-transition>
        <v-btn
          v-show="showBackToTop"
          fab
          fixed
          bottom
          right
          color="primary"
          @click="scrollToTop"
          class="back-to-top-btn"
        >
          <v-icon>mdi-chevron-up</v-icon>
        </v-btn>
      </v-fab-transition>
    </v-container>
  </v-main>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      showBackToTop: false,
      sections: [
        { id: 'introduction', title: 'Introduction' },
        { id: 'information-collect', title: '1. Information You May Provide' },
        { id: 'how-we-use', title: '2. How Your Information Is Used' },
        { id: 'how-we-share', title: '3. How Information May Be Shared' },
        { id: 'data-security', title: '4. Data Security' },
        { id: 'your-rights', title: '5. Your Rights and Choices' },
        { id: 'data-retention', title: '6. Data Retention' },
        { id: 'childrens-privacy', title: "7. Children's Privacy" },
        { id: 'changes', title: '8. Changes to This Policy' },
        { id: 'contact', title: '9. Contact Us' }
      ],
      personalInfo: [
        '<strong>Name</strong> (first name, middle name, last name)',
        '<strong>Phone Number</strong> (primary identifier for account creation and authentication)',
        '<strong>Email Address</strong> (optional)',
        '<strong>Date of Birth</strong>',
        '<strong>Gender</strong>',
        '<strong>Profile Photo</strong> (optional)',
        '<strong>Username</strong>'
      ],
      healthInfo: [
        '<strong>Blood Group</strong>',
        '<strong>Weight and Height</strong>',
        '<strong>Diet Preferences</strong> (dietary restrictions and preferences)',
        '<strong>Lifestyle Information</strong> (such as smoking status or alcohol consumption)',
        '<strong>Allergies, Intolerances, and Sensitivities</strong>',
        '<strong>Current Medications and Medication History</strong>',
        '<strong>Medical History</strong> (past medical conditions and treatments)',
        '<strong>Surgical History</strong> (previous surgeries)',
        '<strong>Symptoms and Health Concerns</strong> discussed during consultations',
        '<strong>Prescriptions</strong> issued through the Platform',
        '<strong>Diagnoses, Treatment Plans, and Consultation Notes</strong> provided by healthcare professionals',
        '<strong>Medical Documents</strong> uploaded by you, including prescriptions, test reports, and medical certificates',
        '<strong>Consultation Records and Follow-Up Information</strong>',
        '<strong>Dependent or Family Profile Information</strong>, including medical information you choose to provide for family members or dependents managed through your account'
      ],
      financialInfo: [
        '<strong>Payment Transaction Details</strong> (amount, transaction ID, payment method, and transaction date)',
        '<strong>Wallet Balance and Transaction History</strong>',
        '<strong>Order and Purchase History</strong>',
        '<strong>Billing Information</strong> (consultation charges, product costs, delivery fees)',
        '<strong>Refund, Cancellation, and Settlement Records</strong> (where applicable)'
      ],
      communicationInfo: [
        '<strong>Chat Messages</strong> exchanged with healthcare professionals during consultations',
        '<strong>Support Ticket Messages</strong> submitted to customer support',
        '<strong>File Attachments</strong> shared in chats or support requests',
        '<strong>Consultation Communications</strong>, including chat, audio, video, or follow-up interactions conducted through the Platform',
        '<strong>Video/Audio Call Metadata</strong> (such as call duration, timestamps, and call type)'
      ],
      deviceInfo: [
        '<strong>Device Identifiers</strong> (such as FCM token for push notifications)',
        '<strong>Device Type and Platform Information</strong> (Android/iOS version)',
        '<strong>App Version</strong>',
        '<strong>IP Address</strong>',
        '<strong>Browser Type and Operating System Information</strong>',
        '<strong>Session Logs and Authentication Records</strong>',
        '<strong>Technical Logs</strong> related to app crashes, errors, and API activity'
      ],
      usageInfo: [
        '<strong>Consultation History</strong> (past, ongoing, and scheduled appointments)',
        '<strong>Product Browsing and Search History</strong>',
        '<strong>Shopping Cart and Wishlist Activity</strong>',
        '<strong>Order History and Delivery Status</strong>',
        '<strong>Ratings and Reviews</strong> submitted for healthcare professionals or products',
        '<strong>Referral Activity</strong> (if you refer others or were referred)',
        '<strong>App Interaction Data</strong>, including screens viewed, feature usage, and notification interactions'
      ],
      informationUses: [
        {
          title: '2.1 To Provide Healthcare and Wellness Services',
          color: 'blue',
          items: [
            'Connecting users with qualified healthcare professionals',
            'Enabling video, audio, and text consultations',
            'Sharing relevant medical information with selected healthcare professionals',
            'Generating and delivering prescriptions',
            'Facilitating continuity of healthcare records',
            'Scheduling and managing appointments',
            'Sending reminders about upcoming consultations',
            'Providing wellness and yoga-related services'
          ]
        },
        {
          title: '2.2 To Process Transactions',
          color: 'green',
          items: [
            'Processing payments for consultations, products, and services',
            'Managing digital wallet functionality',
            'Generating invoices and receipts',
            'Processing refunds where applicable',
            'Preventing fraudulent transactions and misuse'
          ]
        },
        {
          title: '2.3 To Deliver Products',
          color: 'orange',
          items: [
            'Processing and fulfilling medicine and wellness product orders',
            'Coordinating deliveries to specified addresses',
            'Providing order tracking and delivery updates',
            'Handling returns, replacements, and customer service requests'
          ]
        },
        {
          title: '2.4 To Communicate With Users',
          color: 'purple',
          items: [
            'Sending push notifications regarding appointments, orders, and important updates',
            'Responding to support requests and inquiries',
            'Sending transactional communications such as OTPs, booking confirmations, and payment receipts',
            'Providing optional wellness tips, reminders, and educational content through "Taara" notifications, subject to notification preferences'
          ]
        },
        {
          title: '2.5 To Improve Services',
          color: 'teal',
          items: [
            'Understanding Platform usage patterns and user experience',
            'Identifying and resolving technical issues',
            'Developing new features and services',
            'Conducting internal analytics, research, and quality improvement'
          ]
        },
        {
          title: '2.6 To Ensure Safety and Security',
          color: 'red',
          items: [
            'Verifying user identity through phone number and OTP authentication',
            'Detecting and preventing fraud, abuse, and unauthorized activity',
            'Enforcing Platform policies and Terms of Service',
            'Complying with legal and regulatory obligations',
            'Protecting the safety, rights, and security of users, healthcare professionals, staff, and the Platform'
          ]
        }
      ],
      sharedWithDoctors: [
        'Name, age, gender, and contact information',
        'Medical history, surgical history, and current symptoms',
        'Weight, height, blood group, and diet preferences',
        'Lifestyle information voluntarily shared by users',
        'Previous prescriptions and medical documents uploaded by users',
        'Consultation chats, notes, and follow-up information'
      ],
      serviceProviders: [
        {
          name: 'ZEGOCLOUD',
          icon: 'mdi-video',
          color: 'blue',
          purpose: 'Video and audio consultation infrastructure',
          privacyUrl: 'https://www.zegocloud.com/privacy-policy'
        },
        {
          name: 'Razorpay',
          icon: 'mdi-credit-card',
          color: 'green',
          purpose: 'Payment processing',
          privacyUrl: 'https://razorpay.com/privacy/'
        },
        {
          name: 'Firebase Cloud Messaging',
          icon: 'mdi-bell',
          color: 'orange',
          purpose: 'Push notifications',
          privacyUrl: 'https://firebase.google.com/support/privacy'
        }
      ],
      technicalSecurity: [
        '<strong>HTTPS (SSL/TLS) encryption</strong> for data transmission',
        '<strong>Secure communication protocols</strong> for real-time messaging and consultations',
        '<strong>Industry-standard protection measures</strong> for sensitive information',
        '<strong>OTP-based authentication</strong> and authenticated API access mechanisms',
        '<strong>PCI-DSS compliant payment processing</strong> through trusted payment providers'
      ],
      orgSecurity: [
        'Restricted access to authorized personnel only',
        'Confidentiality obligations for employees, contractors, and partners',
        'Security monitoring and periodic reviews',
        'Incident response procedures for potential security events'
      ],
      userSecurity: [
        'Keep phone numbers and OTPs confidential',
        'Avoid sharing accounts with others',
        'Log out from shared or public devices',
        'Report suspicious activity immediately through support channels'
      ],
      userRights: [
        {
          title: 'Access and Updates',
          icon: 'mdi-eye',
          color: 'blue',
          description: [
            'Certain profile details, addresses, medical information, consultations, and account-related information may be viewed or updated through the Platform.'
          ]
        },
        {
          title: 'Deletion Requests',
          icon: 'mdi-delete',
          color: 'red',
          description: [
            'Users may remove saved delivery addresses, clear wishlist items, or request account deletion by contacting Care@healthytantra.com.',
            'Verified deletion requests are generally processed within a reasonable timeframe, subject to legal and operational retention requirements.',
            'Certain medical, transactional, operational, or legally required records may continue to be retained for healthcare continuity, fraud prevention, dispute resolution, taxation, regulatory compliance, or other lawful purposes.'
          ]
        },
        {
          title: 'Communication Preferences',
          icon: 'mdi-bell-off',
          color: 'orange',
          description: [
            'Users may disable push notifications through device or Platform settings.',
            'Opt out of non-essential notifications while continuing to receive important transactional or security-related communications.'
          ]
        },
        {
          title: 'Data Portability',
          icon: 'mdi-export',
          color: 'green',
          description: [
            'Requests for a copy of eligible personal information in a structured and commonly used format may be submitted by contacting Care@healthytantra.com.'
          ]
        },
        {
          title: 'Withdrawal of Consent',
          icon: 'mdi-hand-back-left',
          color: 'purple',
          description: [
            'Where processing relies on consent, consent may be withdrawn at any time, subject to legal or operational limitations.',
            'Withdrawal of consent will not affect the lawfulness of activities conducted before such withdrawal.'
          ]
        }
      ],
      retentionReasons: [
        'Provide services and maintain Platform functionality',
        'Comply with healthcare, legal, tax, accounting, and regulatory obligations',
        'Resolve disputes and enforce agreements',
        'Maintain healthcare and transaction records where required by law',
        'Prevent fraud, abuse, or misuse of the Platform'
      ]
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    handleScroll() {
      this.showBackToTop = window.scrollY > 300;
    }
  }
};
</script>

<style scoped>
.privacy-policy-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  background-color: #f5f6fa;
}

.policy-header {
  text-align: center;
  padding: 32px 0;
}

.policy-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 16px;
}

.policy-meta {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.toc-card {
  border-radius: 12px !important;
}

.sticky-toc {
  position: sticky;
  top: 80px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.toc-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.toc-item:hover {
  background-color: rgba(25, 118, 210, 0.08);
}

.toc-link {
  font-size: 0.9rem;
  color: #2c3e50;
}

.policy-content {
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
}

.section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 3px solid #1976d2;
}

.subsection-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c5f61;
  margin-top: 24px;
  margin-bottom: 12px;
}

.policy-text {
  font-size: 1rem;
  line-height: 1.7;
  color: #34495e;
  margin-bottom: 16px;
  text-align: justify;
}

.info-list {
  background-color: transparent !important;
  margin-bottom: 16px;
}

.use-case {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #1976d2;
}

.right-card {
  height: 100%;
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.right-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.contact-card {
  background-color: #e8f4f5;
  border: 2px solid #1976d2;
  border-radius: 12px !important;
}

.contact-details {
  padding: 16px 0;
}

.contact-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 1rem;
}

.contact-item strong {
  margin-left: 8px;
  margin-right: 8px;
}

.policy-link {
  color: #1976d2;
  text-decoration: none;
  border-bottom: 1px solid #1976d2;
  transition: all 0.2s;
}

.policy-link:hover {
  color: #0d47a1;
  border-bottom-color: #0d47a1;
}

.policy-footer {
  margin-top: 48px;
  padding-top: 24px;
}

.back-to-top-btn {
  z-index: 100;
}

/* Responsive Design */
@media (max-width: 960px) {
  .sticky-toc {
    position: relative;
    top: 0;
    max-height: none;
    margin-bottom: 24px;
  }

  .policy-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.5rem;
  }

  .subsection-title {
    font-size: 1.2rem;
  }
}

@media (max-width: 600px) {
  .privacy-policy-container {
    padding: 16px;
  }

  .policy-title {
    font-size: 1.75rem;
  }

  .policy-meta {
    flex-direction: column;
    align-items: center;
  }

  .section-title {
    font-size: 1.3rem;
  }

  .subsection-title {
    font-size: 1.1rem;
  }

  .policy-text {
    font-size: 0.95rem;
  }
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar for TOC */
.sticky-toc::-webkit-scrollbar {
  width: 6px;
}

.sticky-toc::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.sticky-toc::-webkit-scrollbar-thumb {
  background: #1976d2;
  border-radius: 10px;
}

.sticky-toc::-webkit-scrollbar-thumb:hover {
  background: #0d47a1;
}
</style>
